import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import hero from '../../../assets/images/public-ghg-hero.png'
import gaiaTheme from '../../../root-components/theme'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import {
  GAIA_PDF_PAGE_HEIGHT_PX,
  getGaiaReportSectionShellSx,
} from '../../public-reports/components/gaia-report-web-page-pdf-styles'
import { ImageDisplay } from '../../../components/presentation-components/display-elements-components/image-display'
import type { IColorPalatte } from '../shared-services/project-reports.model'
import { useReportContentOverride } from '../../public-reports/shared-services/gaia-report-content'

export const ProjectReportWebPageHero: FunctionComponent<{
  colorPalatte: IColorPalatte
  projectName: string
  organisationName: string
  organisationLogoFilepath?: string | null
  coverImageFilepath?: string | null
  textColor?: string
  customTitle?: string | null
  startDate?: string | null
  endDate?: string | null
  projectLabel: string
}> = ({ colorPalatte, projectName, organisationName, organisationLogoFilepath, coverImageFilepath, textColor, customTitle, startDate, endDate, projectLabel }) => {
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const pdfExportMode = usePdfExportMode()
  const backgroundImage = coverImageFilepath || hero
  const titleMaxWidth = isMobileModeSm ? '100%' : isMobileModeMd ? '75%' : '80%'
  const eyebrow = useReportContentOverride('cover.eyebrow')

  const formatDate = (date: string | null | undefined) => {
    if (!date) return null
    try {
      return new Date(date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    } catch {
      return null
    }
  }

  const dateRange = startDate
    ? `${formatDate(startDate)}${endDate ? ` – ${formatDate(endDate)}` : ''}`
    : null

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({
        backgroundColor: colorPalatte.dark,
        pdfExport: pdfExportMode,
        webBottomPadding: 0,
        webBorderRadius: '0',
        overflow: 'hidden',
      }),
      pt: 0, pb: pdfExportMode ? 0 : 5, pl: 0, pr: 0,
    }}>
      <Stack sx={{ minHeight: pdfExportMode ? `${GAIA_PDF_PAGE_HEIGHT_PX}px` : '100vh' }}>
        <Box sx={{
          height: isMobileModeSm ? '56px' : '64px',
          backgroundColor: 'common.white',
          px: isMobileModeSm ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          {organisationLogoFilepath ? (
            <ImageDisplay url={organisationLogoFilepath} alt='Company logo' customHeight={isMobileModeSm ? '32px' : '40px'} customWidth={isMobileModeSm ? '140px' : '180px'} />
          ) : (
            <Box />
          )}
          <Typography variant={isMobileModeSm ? 'body1' : 'h6'} sx={{ color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
            {organisationName}
          </Typography>
        </Box>
        <Stack sx={{
          flex: 1,
          backgroundColor: colorPalatte.dark,
          p: isMobileModeSm ? 2 : 4,
        }}>
          <Stack display='flex' justifyContent='center' sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: isMobileModeSm ? '24px' : '32px',
            overflow: 'hidden',
            px: isMobileModeSm ? 3 : 6,
            backgroundImage: `linear-gradient(90deg, rgba(7, 24, 44, 0.6) 0%, rgba(7, 24, 44, 0.2) 45%, rgba(7, 24, 44, 0.05) 100%), url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}>
            <Typography variant='overline' sx={{ color: textColor || 'white', opacity: 0.8 }}>
              {eyebrow ?? `${projectLabel} Report`}
            </Typography>
            <Typography
              variant={isMobileModeSm ? 'h3' : isMobileModeMd ? 'h2' : 'h1'}
              sx={{ color: textColor || 'white', fontWeight: 900, maxWidth: titleMaxWidth }}
            >
              {customTitle || projectName}
            </Typography>
            {!customTitle && dateRange && (
              <Typography
                variant={isMobileModeSm ? 'h5' : isMobileModeMd ? 'h4' : 'h3'}
                sx={{ color: textColor || 'white', mt: 1 }}
              >
                {dateRange}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
